import $ from "jquery";
import _ from "underscore";
import TreeNodeRenderer from "./TreeNodeRenderer";
import ExpandController from "./ExpandController";
import ScrollUtils from "../../util/ScrollUtils";
import {fixIScrollInnerWheelEvent} from '../../atBrowserUtils'

export default class Tree {
    constructor(rootNode, rootVisible, collapsedNodeIds) {
        this.rootNode = rootNode;
        this.expandController = this.createExpandController(collapsedNodeIds);
        this.nodeRenderers = [];
        this.defaultNodeRenderer = this.createDefaultNodeRenderer();
        this.activeFilter = null;
        this.previousFilterResults = null;
        this.previousFilterResultsRoots = null;
        this.treeContainer = null;
        this.rootVisible = rootVisible;
        this.uiMap = {};
    }

    getRootNode() {
        return this.rootNode;
    }

    isRootVisible() {
        return this.rootVisible;
    }

    getAllNodes() {
        const nodes = [];

        function collect(node) {
            nodes.push(node);
            _.each(node.getChildren(), function(child) {
                collect(child)
            });
        }

        collect(this.rootNode);
        return nodes;
    }

    createExpandController(collapsedNodeIds) {
        return new ExpandController(this, collapsedNodeIds);
    }

    getExpandController() {
        return this.expandController;
    }

    setNodeRenderers(nodeRenderers) {
        this.nodeRenderers = nodeRenderers;
    }

    createDefaultNodeRenderer() {
        return new TreeNodeRenderer(this);
    }

    getRenderer(treeNode) {
        const renderer = this.nodeRenderers[treeNode.getType()];
        return renderer ? renderer : this.defaultNodeRenderer;
    }

    updateTreeContainerClasses() {
        const classes = ["treeContainer"];
        if (this.activeFilter) {
            classes.push("filtered");
            const filterClasses = this.activeFilter.getFilterClasses();
            for (let i = 0; i < filterClasses.length; i++)
                classes.push(filterClasses[i]);
        }
        else {
            classes.push("notFiltered");
            if (!this.rootNode.hasChildren()) {
                classes.push("empty");
            }
        }
        this.treeContainer.className = classes.join(" ");
    }

    //  TODO: Actually adds empty message panels. There should be some messages builder or common messages logic.
    decorateTreePanel(treePanel) {
        //do nothing
    }

    createTreeContainer() {
        return document.createElement("div");
    }

    buildDOM(containerEl, initialFilter) {
        if (initialFilter)
            this.applyFilterNoUpdate(initialFilter);

        this.treeContainer = this.createTreeContainer();

        this.decorateTreePanel(this.treeContainer);
        this.updateTreeContainerClasses();

        if (this.rootVisible)
            this.buildDOMForNode(this.treeContainer, this.rootNode);
        else
            this.buildDOMForChildren(this.treeContainer, this.rootNode);

        containerEl.appendChild(this.treeContainer);

        this.customScroll = ScrollUtils.addVerticalScroll($(containerEl), {isFaded: false});
        fixIScrollInnerWheelEvent($(containerEl))
    }

    buildDOMForChildren(targetEl, treeNode) {
        const children = treeNode.getChildren();
        const childrenContainer = ((treeNode.getParent() || this.rootVisible) && children.length > 0) ? this.createChildrenContainerDiv(targetEl) : targetEl;
        for (let i = 0; i < children.length; i++)
            this.buildDOMForNode(childrenContainer, children[i]);
    }

    createChildrenContainerDiv(targetEl) {
        const childrenContainerDiv = document.createElement("div");
        childrenContainerDiv.className = "childrenContainer";
        targetEl.appendChild(childrenContainerDiv);
        return childrenContainerDiv;
    }

    buildDOMForNode(targetEl, treeNode) {
        const renderer = this.getRenderer(treeNode);

        const nodeDiv = document.createElement("div");
        this.getUIObjectForNode(treeNode).nodeEl = nodeDiv;

        const contentDiv = document.createElement("div");
        contentDiv.className = "content";

        renderer.generateContent(treeNode, contentDiv);
        renderer.updateUI(treeNode);

        nodeDiv.appendChild(contentDiv);

        this.buildDOMForChildren(nodeDiv, treeNode);

        targetEl.appendChild(nodeDiv);
    }

    collapseAll() {
        if (this.rootVisible)
            this.expandController.collapseSubtree(this.rootNode);
        else
            this.expandController.collapseChildren(this.rootNode);
    }

    expandAll() {
        if (this.rootVisible)
            this.expandController.expandSubtree(this.rootNode);
        else
            this.expandController.expandChildren(this.rootNode);
    }

    _updateUIForTreeNode(treeNode) {
        if ((treeNode.getParent() != null) || this.rootVisible) {
            this.getRenderer(treeNode).updateUI(treeNode);
        }
    }

    updateUIForSingleTreeNode(treeNode) {
        this._updateUIForTreeNode(treeNode)
        this.refreshScroller();
    }

    updateUIForTreeNodeSet(treeNodeSet) {
        for (let level in treeNodeSet.map) {
            //noinspection JSUnfilteredForInLoop
            const levelObj = treeNodeSet.map[level];
            for (let id in levelObj) {
                //noinspection JSUnfilteredForInLoop
                this._updateUIForTreeNode(levelObj[id]);
            }
        }

        this.refreshScroller();
    }

    resetFilter() {
        if (this.previousFilterResults) {
            this.activeFilter = null;
            this.updateTreeContainerClasses();
            this.updateUIForTreeNodeSet(this.previousFilterResults.union(this.previousFilterResultsRoots));
            this.previousFilterResults = null;
            this.previousFilterResultsRoots = null;
        }
    }

    getActiveFilter() {
        return this.activeFilter;
    }

    hasActiveFilter() {
        return this.activeFilter != null;
    }

    doesMatchFilterCriteria(treeNode) {
        return this.previousFilterResults.contains(treeNode);
    }

    doesContainChildrenMatchingFilterCriteria(treeNode) {
        return this.previousFilterResultsRoots.contains(treeNode);
    }

    applyFilterNoUpdate(filter) {
        let newFilterResults = null;
        if (filter) {
            filter.filterNodes();
            newFilterResults = filter.getResults();
        }
        if (newFilterResults) {
            this.activeFilter = filter;
            if (this.previousFilterResults) {
                const nodesToUpdate = this.previousFilterResults;
                nodesToUpdate.addAll(newFilterResults);
                nodesToUpdate.addAll(nodesToUpdate.makePathsNodeSet());
                this.previousFilterResults = newFilterResults;
                this.previousFilterResultsRoots = newFilterResults.makePathsNodeSet();
                return nodesToUpdate;
            }
            else {
                this.previousFilterResults = newFilterResults;
                this.previousFilterResultsRoots = newFilterResults.makePathsNodeSet();
                return this.previousFilterResults.union(this.previousFilterResultsRoots);
            }
        }
        else {
            this.resetFilter();
            return null;
        }
    }

    applyFilter(filter) {
        const nodesToUpdate = this.applyFilterNoUpdate(filter);
        if (nodesToUpdate) {
            this.updateTreeContainerClasses();
            this.updateUIForTreeNodeSet(nodesToUpdate);
        }
    }

    getUIObjectForNode(treeNode) {
        let uiObject = this.uiMap[treeNode.id];
        if (!uiObject) {
            uiObject = {};
            this.uiMap[treeNode.id] = uiObject;
        }
        return uiObject;
    }

    refreshScroller() {
        ScrollUtils.refreshCustomScroll(this.customScroll);
    }
}



// WEBPACK FOOTER //
// ./web/js/hierarchySelector/tree/Tree.js